/** Local calendar date key (YYYY-MM-DD) for company holiday lookup. */
export function calendarDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isUtcMidnight(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/** Calendar day key for DevExtreme scheduler cells (month cells are often UTC midnight). */
export function schedulerCalendarDateKey(date: Date): string {
  if (isUtcMidnight(date)) {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return calendarDateKey(date);
}

/** Day-of-month label for DevExtreme scheduler cells (handles UTC-midnight month cells). */
export function schedulerDayOfMonth(date: Date): number {
  if (isUtcMidnight(date)) return date.getUTCDate();
  return date.getDate();
}

const WEEKDAY_HEADER = /^(Sun|Mon|Tue|Wed|Thu|Fri|Sat)$/i;

/** Month-view column headers pass day abbreviations as `text`, not day numbers. */
export function isSchedulerWeekdayHeaderText(text: string | undefined | null): boolean {
  if (!text) return false;
  return WEEKDAY_HEADER.test(text.trim());
}

export function holidayNameForDate(date: Date, lookup: Map<string, string>): string | undefined {
  return lookup.get(calendarDateKey(date));
}

export function holidayNameForSchedulerDate(date: Date, lookup: Map<string, string>): string | undefined {
  return lookup.get(schedulerCalendarDateKey(date));
}

export function isHolidayDate(date: Date, lookup: Map<string, string>): boolean {
  return lookup.has(calendarDateKey(date));
}

type SchedulerTemplateModel<T> = T | { data?: T } | null | undefined;

function unwrapSchedulerCellModel<T extends object>(model: SchedulerTemplateModel<T>): T | null {
  if (!model || typeof model !== 'object') return null;
  if ('data' in model && model.data && typeof model.data === 'object') {
    return model.data as T;
  }
  return model as T;
}

function parseSchedulerCellDate(raw: Date | string | undefined): Date | null {
  if (raw == null) return null;
  const date = raw instanceof Date ? raw : new Date(String(raw));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** DevExtreme dataCellRender passes cell fields directly; some wrappers nest under `data`. */
export function dateFromSchedulerDataCellModel(
  model: SchedulerTemplateModel<{ startDate?: Date | string }>
): Date | null {
  const cell = unwrapSchedulerCellModel(model);
  return parseSchedulerCellDate(cell?.startDate);
}

/** DevExtreme dateCellRender uses `date` (not startDate) on the cell payload. */
export function dateFromSchedulerDateCellModel(
  model: SchedulerTemplateModel<{ date?: Date | string; text?: string }>
): { date: Date | null; text?: string } {
  const cell = unwrapSchedulerCellModel(model);
  return {
    date: parseSchedulerCellDate(cell?.date),
    text: cell?.text
  };
}
